// Generic time-series utilities
//
// All functions operate over series shaped as:
//   [{ id: 'yyyy-mm-dd', value: number }]
// and return a series with the same ids and smoothed/derived values.

// series: [{ id: 'yyyy-mm-dd', value: number }]
/**
 * Simple Moving Average (SMA)
 * Computes the arithmetic mean over a sliding window. For the first points,
 * uses the average of the available prefix so the series length is preserved.
 * @param {Array<{id:string,value:number}>} series
 * @param {number} windowSize - window length in samples (e.g., 7 or 14)
 * @returns {Array<{id:string,value:number}>}
 */
export function computeSMA(series, windowSize) {
  if (!Array.isArray(series) || windowSize <= 1) return series || [];
  const out = [];
  let sum = 0;
  const q = [];
  for (let i = 0; i < series.length; i++) {
    const v = Number(series[i].value) || 0;
    sum += v; q.push(v);
    if (q.length > windowSize) sum -= q.shift();
    const avg = q.length === windowSize ? (sum / windowSize) : (sum / q.length);
    out.push({ id: series[i].id, value: avg });
  }
  return out;
}

/**
 * Exponentially Weighted Moving Average (EWMA)
 * Recurrence: s_t = α·x_t + (1-α)·s_{t-1}
 * Smooths noise while reacting faster than long SMA windows.
 * @param {Array<{id:string,value:number}>} series
 * @param {number} alpha - smoothing factor in (0,1]; higher = more reactive
 * @returns {Array<{id:string,value:number}>}
 */
export function computeEWMA(series, alpha = 0.3) {
  if (!Array.isArray(series) || series.length === 0) return series || [];
  const out = [];
  let prev = Number(series[0].value) || 0;
  out.push({ id: series[0].id, value: prev });
  for (let i = 1; i < series.length; i++) {
    const v = Number(series[i].value) || 0;
    const ewma = alpha * v + (1 - alpha) * prev;
    out.push({ id: series[i].id, value: ewma });
    prev = ewma;
  }
  return out;
}

/**
 * Difference over a fixed lag (days).
 * For each point t, returns x_t - x_{t-days}. Leading values fallback to 0.
 * @param {Array<{id:string,value:number}>} series
 * @param {number} days - positive integer lag
 * @returns {Array<{id:string,value:number}>}
 */
export function diffOver(series, days) {
  if (!Array.isArray(series) || series.length === 0) return [];
  const out = [];
  for (let i = 0; i < series.length; i++) {
    const cur = series[i];
    const back = i - days;
    if (back >= 0) {
      const delta = Number(cur.value || 0) - Number(series[back].value || 0);
      out.push({ id: cur.id, value: delta });
    } else {
      out.push({ id: cur.id, value: 0 });
    }
  }
  return out;
}

/**
 * Rolling Median over a sliding window.
 * More robust than SMA against outliers. For the first points uses the
 * available prefix window.
 * @param {Array<{id:string,value:number}>} series
 * @param {number} windowSize - window length in samples (odd recommended)
 * @returns {Array<{id:string,value:number}>}
 */
export function computeRollingMedian(series, windowSize) {
  if (!Array.isArray(series) || windowSize <= 1) return series || [];
  const out = [];
  const window = [];
  for (let i = 0; i < series.length; i++) {
    window.push(Number(series[i].value) || 0);
    if (window.length > windowSize) window.shift();
    const sorted = [...window].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const median = sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
    out.push({ id: series[i].id, value: median });
  }
  return out;
}

/**
 * LOWESS (LOcally WEighted Scatterplot Smoothing)
 * Local linear regression with tricube weights over a neighborhood defined
 * by `span` fraction of the series length. Produces a smooth trend that
 * adapts to slow changes while ignoring short-term noise.
 * @param {Array<{id:string,value:number}>} series
 * @param {number} span - neighborhood size as fraction [0.05, 0.9], e.g. 0.3
 * @returns {Array<{id:string,value:number}>}
 */
export function computeLOWESS(series, span = 0.3) {
  if (!Array.isArray(series) || series.length === 0) return series || [];
  const n = series.length;
  const m = Math.max(2, Math.min(n, Math.round(Math.max(0.05, Math.min(0.9, span)) * n)));
  const x = Array.from({ length: n }, (_, i) => i);
  const y = series.map(s => Number(s.value) || 0);
  const yhat = new Array(n).fill(0);
  for (let i = 0; i < n; i++) {
    // Determine neighborhood size m around i
    const left = Math.max(0, i - Math.floor((m - 1) / 2));
    const right = Math.min(n - 1, left + m - 1);
    const start = Math.max(0, right - m + 1);
    const end = right;
    // Compute distances and tricube weights
    const dmax = Math.max(1e-9, Math.max(Math.abs(x[i] - x[start]), Math.abs(x[end] - x[i])));
    let sw = 0, swx = 0, swy = 0, swxx = 0, swxy = 0;
    for (let j = start; j <= end; j++) {
      const u = Math.abs(x[j] - x[i]) / dmax;
      const w = Math.pow(1 - Math.pow(u, 3), 3);
      const X = x[j];
      const Y = y[j];
      sw += w;
      swx += w * X;
      swy += w * Y;
      swxx += w * X * X;
      swxy += w * X * Y;
    }
    // Solve weighted linear regression for a + b x
    const denom = sw * swxx - swx * swx;
    let a = 0, b = 0;
    if (Math.abs(denom) > 1e-12) {
      b = (sw * swxy - swx * swy) / denom;
      a = (swy - b * swx) / sw;
    } else {
      a = swy / Math.max(sw, 1e-12);
      b = 0;
    }
    yhat[i] = a + b * x[i];
  }
  return series.map((s, i) => ({ id: s.id, value: yhat[i] }));
}

/**
 * Resample a time series to daily frequency using linear interpolation between known points.
 * - Input must be ascending by id 'yyyy-mm-dd'
 * - Fills missing days between consecutive points; does not extrapolate beyond endpoints
 * @param {Array<{id:string,value:number}>} series
 * @returns {Array<{id:string,value:number}>}
 */
export function resampleLinearDaily(series) {
  if (!Array.isArray(series) || series.length === 0) return series || [];
  const out = [];
  const parseId = (id) => {
    const [y, m, d] = String(id).split('-').map(Number);
    return new Date(Date.UTC(y, (m || 1) - 1, d || 1));
  };
  const fmtId = (dt) => `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2,'0')}-${String(dt.getUTCDate()).padStart(2,'0')}`;
  for (let i = 0; i < series.length - 1; i++) {
    const a = series[i];
    const b = series[i + 1];
    const da = parseId(a.id);
    const db = parseId(b.id);
    // ensure ordering
    if (!(da <= db)) continue;
    const days = Math.max(1, Math.round((db - da) / 86400000));
    for (let k = 0; k < days; k++) {
      const t = k / days; // [0,1)
      const dt = new Date(da.getTime());
      dt.setUTCDate(dt.getUTCDate() + k);
      const id = fmtId(dt);
      const val = Number(a.value || 0) + (Number(b.value || 0) - Number(a.value || 0)) * t;
      // Avoid duplicate ids
      if (!out.length || out[out.length - 1].id !== id) out.push({ id, value: val });
    }
  }
  // push last point
  const last = series[series.length - 1];
  if (!out.length || out[out.length - 1].id !== last.id) out.push({ id: last.id, value: Number(last.value || 0) });
  return out;
}


